import { FC, useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import { IntensityPerTonMonthlyResponse } from './types';
import { seriesPrimary, chartChrome } from '../../utils/chartColors';
import ChartErrorState from './ChartErrorState';
import { formatDecimal, monthAbbreviations } from '../../utils/localeFormat';
import './IntensityPerTonMonthlyChart.scss';

interface IntensityPerTonMonthlyChartProps {
  data: IntensityPerTonMonthlyResponse | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
}


const IntensityPerTonMonthlyChart: FC<IntensityPerTonMonthlyChartProps> = ({ data, isLoading, error, onRetry }) => {
  const { t, i18n } = useTranslation();
  // optional: formatters fall back to the i18next fallbackLng when absent,
  // which must not become a crash if useTranslation yields no i18n
  const lng = i18n?.language;
  // memoised so it can be an effect dependency without re-firing every render
  const monthNames = useMemo(() => monthAbbreviations(lng), [lng]);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  const chartCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  useEffect(() => {
    if (!chartNode || !data || data.data.length === 0) {
      return;
    }

    if (!chartInstance.current || chartInstance.current.getDom() !== chartNode) {
      chartInstance.current?.dispose();
      chartInstance.current = echarts.init(chartNode);
    }

    const months = data.data.map((d) => {
      const monthIndex = parseInt(d.month.split('-')[1], 10) - 1;
      return monthNames[monthIndex] || d.month;
    });
    const values = data.data.map((d) => d.intensityKgPerTon);

    const chrome = chartChrome();
    const primary = seriesPrimary();

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          return `${p.name}<br/>${p.marker} ${formatDecimal(Number(p.value), 2, lng)} kg/ton`;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        top: '10%',
        bottom: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: { fontSize: 12, color: chrome.muted, rotate: 45 },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      yAxis: {
        type: 'value',
        name: String(t('cpi.axis.perTon')),
        nameTextStyle: { color: chrome.muted, fontSize: 11 },
        axisLabel: { color: chrome.muted, formatter: (v: number) => formatDecimal(v, 1, lng) },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { type: 'dotted', color: chrome.grid } },
      },
      series: [
        {
          type: 'line',
          step: 'end',
          data: values,
          lineStyle: { color: primary, width: 2.5 },
          itemStyle: { color: primary },
          showSymbol: false,
        },
      ],
    };

    chartInstance.current.setOption(option, true);
  }, [data, chartNode, lng, monthNames, t]);

  useEffect(() => {
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (error && !isLoading) {
    return (
      <div className="intensity-per-ton-monthly-chart">
        <h3 className="intensity-per-ton-monthly-chart__title">{t('cpi.analytics.intensityPerTon')}</h3>
        <ChartErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="intensity-per-ton-monthly-chart intensity-per-ton-monthly-chart--loading">
        <h3 className="intensity-per-ton-monthly-chart__title">{t('cpi.analytics.intensityPerTon')}</h3>
        <div className="intensity-per-ton-monthly-chart__skeleton" />
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="intensity-per-ton-monthly-chart">
        <h3 className="intensity-per-ton-monthly-chart__title">{t('cpi.analytics.intensityPerTon')}</h3>
        <p className="chart-no-data">{t('cpi.noDataAvailableForChart')}</p>
      </div>
    );
  }

  return (
    <div className="intensity-per-ton-monthly-chart">
      <h3 className="intensity-per-ton-monthly-chart__title">{t('cpi.analytics.intensityPerTon')}</h3>
      <div ref={chartCallbackRef} style={{ height: '350px', width: '100%' }} />
    </div>
  );
};

export default IntensityPerTonMonthlyChart;
